import React, { useCallback, useEffect, useMemo, useState } from "react";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import {
  ADD_QUICK_SERVICE_CHOICE_GROUP_ITEM,
  CREATE_QUICK_SERVICE_CHOICE_GROUP,
  CREATE_QUICK_SERVICE_COMBO,
  CREATE_QUICK_SERVICE_COMBO_GROUP,
  UPDATE_QUICK_SERVICE_CHOICE_GROUP_ITEM,
  UPDATE_QUICK_SERVICE_COMBO,
} from "src/constants/mutations";
import { GET_QUICK_SERVICE_SETUP } from "src/constants/queries";
import { useAuth } from "src/contexts/auth-provider";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";

type MenuItemLite = {
  correlationId: string;
  englishName?: string;
  priceCents?: number;
  isQuickServiceChoiceItem?: boolean;
  active?: boolean;
};

type CategoryLite = {
  correlationId: string;
  name: string;
  items?: MenuItemLite[];
};

type QuickServiceChoiceGroup = {
  correlationId: string;
  name: string;
  description?: string;
  renderSequence?: number;
  items?: Array<{
    correlationId: string;
    itemCorrelationId: string;
    displayNameSnapshot?: string;
    priceDeltaCents?: number;
    item?: MenuItemLite;
  }>;
};

type QuickServiceCombo = {
  correlationId: string;
  storeCorrelationId: string;
  name: string;
  priceCents?: number;
  taxRate?: number;
  renderSequence?: number;
  comboGroups?: Array<{
    correlationId: string;
    quickServiceComboCorrelationId: string;
    displayName?: string;
    minSelections: number;
    maxSelections: number;
    choiceGroup?: { correlationId: string; name: string };
  }>;
};

const cents = (value?: number) => `$${((value ?? 0) / 100).toFixed(2)}`;
const percent = (value?: number) => `${(((value ?? 0) * 100)).toFixed(2)}%`;
const taxRateFromPercent = (value: string) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed / 100 : 0;
};

export default function QuickServiceSetupPage() {
  const { hasRestaurant } = useAuth();
  const { axBe } = useAxiosInterceptor();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<CategoryLite[]>([]);
  const [choiceGroups, setChoiceGroups] = useState<QuickServiceChoiceGroup[]>([]);
  const [combos, setCombos] = useState<QuickServiceCombo[]>([]);
  const [choiceGroupName, setChoiceGroupName] = useState("");
  const [comboName, setComboName] = useState("");
  const [comboPrice, setComboPrice] = useState("");
  const [comboTaxRatePercent, setComboTaxRatePercent] = useState("");
  const [selectedChoiceGroupId, setSelectedChoiceGroupId] = useState("");
  const [selectedChoiceItemId, setSelectedChoiceItemId] = useState("");
  const [selectedComboItemId, setSelectedComboItemId] = useState("");
  const [selectedComboChoiceGroupId, setSelectedComboChoiceGroupId] = useState("");
  const [comboGroupName, setComboGroupName] = useState("");
  const [minSelections, setMinSelections] = useState(1);
  const [maxSelections, setMaxSelections] = useState(1);
  const [editingComboId, setEditingComboId] = useState("");
  const [editingComboName, setEditingComboName] = useState("");
  const [editingComboPrice, setEditingComboPrice] = useState("");
  const [editingComboTaxRatePercent, setEditingComboTaxRatePercent] = useState("");

  const allItems = useMemo(
    () => categories.flatMap((category) => category.items ?? []),
    [categories]
  );
  const selectableChoiceItems = useMemo(
    () => allItems.filter((item) => item.active !== false && item.isQuickServiceChoiceItem === true),
    [allItems]
  );
  const selectedChoiceGroup = useMemo(
    () => choiceGroups.find((group) => group.correlationId === selectedChoiceGroupId),
    [choiceGroups, selectedChoiceGroupId]
  );
  const selectedChoiceGroupItemIds = useMemo(
    () => new Set((selectedChoiceGroup?.items ?? []).map((item) => item.itemCorrelationId)),
    [selectedChoiceGroup?.items]
  );
  const selectableChoiceItemsForSelectedGroup = useMemo(
    () => selectableChoiceItems.filter((item) => !selectedChoiceGroupItemIds.has(item.correlationId)),
    [selectableChoiceItems, selectedChoiceGroupItemIds]
  );

  const loadSetup = useCallback(async () => {
    if (!hasRestaurant?.correlationId) return;
    setLoading(true);
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: GET_QUICK_SERVICE_SETUP,
        variables: {
          storeCorrelationId: hasRestaurant.correlationId,
          activeFilter: "ACTIVE",
        },
      });
      setCategories(data?.data?.categoriesByStoreCorrelationId ?? []);
      setChoiceGroups(data?.data?.quickServiceChoiceGroups ?? []);
      setCombos(data?.data?.quickServiceCombos ?? []);
    } finally {
      setLoading(false);
    }
  }, [axBe, hasRestaurant?.correlationId]);

  useEffect(() => {
    loadSetup();
  }, [loadSetup]);

  useEffect(() => {
    if (
      selectedChoiceItemId &&
      !selectableChoiceItems.some((entry) => entry.correlationId === selectedChoiceItemId)
    ) {
      setSelectedChoiceItemId("");
    }
  }, [selectableChoiceItems, selectedChoiceItemId]);
  useEffect(() => {
    if (
      selectedChoiceItemId &&
      selectedChoiceGroupItemIds.has(selectedChoiceItemId)
    ) {
      setSelectedChoiceItemId("");
    }
  }, [selectedChoiceGroupItemIds, selectedChoiceItemId]);

  const createChoiceGroup = async () => {
    if (!hasRestaurant?.correlationId || !choiceGroupName.trim()) return;
    await axBe.post(GRAPHQL_ENDPOINT, {
      query: CREATE_QUICK_SERVICE_CHOICE_GROUP,
      variables: {
        input: {
          storeCorrelationId: hasRestaurant.correlationId,
          name: choiceGroupName.trim(),
        },
      },
    });
    setChoiceGroupName("");
    await loadSetup();
  };

  const addChoiceItem = async () => {
    if (!selectedChoiceGroupId || !selectedChoiceItemId) return;
    if (selectedChoiceGroupItemIds.has(selectedChoiceItemId)) return;
    const item = selectableChoiceItems.find(
      (entry: MenuItemLite) => entry.correlationId === selectedChoiceItemId
    );
    await axBe.post(GRAPHQL_ENDPOINT, {
      query: ADD_QUICK_SERVICE_CHOICE_GROUP_ITEM,
      variables: {
        input: {
          choiceGroupCorrelationId: selectedChoiceGroupId,
          itemCorrelationId: selectedChoiceItemId,
          displayNameSnapshot: item?.englishName,
          priceDeltaCents: 0,
        },
      },
    });
    setSelectedChoiceItemId("");
    await loadSetup();
  };

  const removeChoiceItem = async (choiceGroupItemCorrelationId: string) => {
    await axBe.post(GRAPHQL_ENDPOINT, {
      query: UPDATE_QUICK_SERVICE_CHOICE_GROUP_ITEM,
      variables: {
        input: {
          correlationId: choiceGroupItemCorrelationId,
          active: false,
        },
      },
    });
    await loadSetup();
  };

  const createCombo = async () => {
    if (!hasRestaurant?.correlationId || !comboName.trim()) return;
    await axBe.post(GRAPHQL_ENDPOINT, {
      query: CREATE_QUICK_SERVICE_COMBO,
      variables: {
        input: {
          storeCorrelationId: hasRestaurant.correlationId,
          name: comboName.trim(),
          priceCents: Math.round(Number(comboPrice || 0) * 100),
          taxRate: taxRateFromPercent(comboTaxRatePercent),
        },
      },
    });
    setComboName("");
    setComboPrice("");
    setComboTaxRatePercent("");
    await loadSetup();
  };

  const startEditingCombo = (combo: QuickServiceCombo) => {
    setEditingComboId(combo.correlationId);
    setEditingComboName(combo.name);
    setEditingComboPrice(((combo.priceCents ?? 0) / 100).toFixed(2));
    setEditingComboTaxRatePercent(((combo.taxRate ?? 0) * 100).toFixed(2));
  };

  const cancelEditingCombo = () => {
    setEditingComboId("");
    setEditingComboName("");
    setEditingComboPrice("");
    setEditingComboTaxRatePercent("");
  };

  const updateCombo = async () => {
    if (!editingComboId || !editingComboName.trim()) return;
    await axBe.post(GRAPHQL_ENDPOINT, {
      query: UPDATE_QUICK_SERVICE_COMBO,
      variables: {
        input: {
          correlationId: editingComboId,
          name: editingComboName.trim(),
          priceCents: Math.round(Number(editingComboPrice || 0) * 100),
          taxRate: taxRateFromPercent(editingComboTaxRatePercent),
        },
      },
    });
    cancelEditingCombo();
    await loadSetup();
  };

  const deleteCombo = async (comboCorrelationId: string) => {
    await axBe.post(GRAPHQL_ENDPOINT, {
      query: UPDATE_QUICK_SERVICE_COMBO,
      variables: {
        input: {
          correlationId: comboCorrelationId,
          active: false,
        },
      },
    });
    if (selectedComboItemId === comboCorrelationId) {
      setSelectedComboItemId("");
    }
    if (editingComboId === comboCorrelationId) {
      cancelEditingCombo();
    }
    await loadSetup();
  };

  const attachChoiceGroupToCombo = async () => {
    if (!selectedComboItemId || !selectedComboChoiceGroupId) return;
    await axBe.post(GRAPHQL_ENDPOINT, {
      query: CREATE_QUICK_SERVICE_COMBO_GROUP,
      variables: {
        input: {
          quickServiceComboCorrelationId: selectedComboItemId,
          choiceGroupCorrelationId: selectedComboChoiceGroupId,
          displayName: comboGroupName.trim() || undefined,
          minSelections,
          maxSelections,
          isRequired: minSelections > 0,
        },
      },
    });
    setComboGroupName("");
    await loadSetup();
  };

  return (
    <div className="flex w-full flex-col gap-5 pb-6">
      <div>
        <h1 className="text-[20px] font-semibold text-[#111827]">Quick Service Setup</h1>
        <p className="mt-1 text-sm text-[#6b7280]">
          Build quick-service plates separately from fixed combos. Create reusable choice groups from normal menu
          items, then attach those groups to each plate with selection rules.
        </p>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <section className="rounded-2xl border border-[#E5E7EB] bg-white p-4 shadow-sm">
          <h2 className="text-base font-semibold text-[#111827]">Choice Groups</h2>
          <p className="mt-1 text-xs text-[#6b7280]">
            Create reusable groups first, then select one group and add menu items to it.
          </p>
          <div className="mt-4 flex gap-2">
            <input
              className="h-10 flex-1 rounded-lg border border-[#D1D5DB] px-3 text-sm"
              value={choiceGroupName}
              onChange={(event) => setChoiceGroupName(event.target.value)}
              placeholder="e.g. Entrees, Sides, Drinks"
            />
            <button className="h-10 rounded-lg bg-[#FD7657] px-4 text-sm font-medium text-white" onClick={createChoiceGroup}>
              Add Group
            </button>
          </div>

          <div className="mt-5 space-y-3">
            {choiceGroups.map((group) => {
              const selected = group.correlationId === selectedChoiceGroupId;
              return (
                <div
                  key={group.correlationId}
                  role="button"
                  tabIndex={0}
                  className={`w-full rounded-xl border p-3 text-left transition ${
                    selected ? "border-[#FD7657] bg-[#FEF5F4]" : "border-[#E5E7EB] bg-white"
                  }`}
                  onClick={() => setSelectedChoiceGroupId(group.correlationId)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setSelectedChoiceGroupId(group.correlationId);
                    }
                  }}
                >
                  <div className="font-medium text-[#111827]">{group.name}</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(group.items ?? []).length === 0 ? (
                      <span className="text-xs text-[#9CA3AF]">No items added yet.</span>
                    ) : (
                      (group.items ?? []).map((item) => (
                        <span
                          key={item.correlationId}
                          className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs text-[#FD7657]"
                        >
                          <span>{item.displayNameSnapshot ?? item.item?.englishName}</span>
                          <button
                            type="button"
                            className="text-[#9CA3AF] hover:text-[#DC2626]"
                            aria-label={`Remove ${item.displayNameSnapshot ?? item.item?.englishName ?? "choice item"}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              removeChoiceItem(item.correlationId);
                            }}
                          >
                            ×
                          </button>
                        </span>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <h3 className="mt-6 text-sm font-semibold text-[#111827]">Add Items To Selected Group</h3>
          <div className="mt-3 grid gap-2">
            <select className="h-10 rounded-lg border border-[#D1D5DB] px-3 text-sm" value={selectedChoiceGroupId} onChange={(event) => setSelectedChoiceGroupId(event.target.value)}>
              <option value="">Select choice group</option>
              {choiceGroups.map((group) => <option key={group.correlationId} value={group.correlationId}>{group.name}</option>)}
            </select>
            <select className="h-10 rounded-lg border border-[#D1D5DB] px-3 text-sm" value={selectedChoiceItemId} onChange={(event) => setSelectedChoiceItemId(event.target.value)}>
              <option value="">Select item to add</option>
              {selectableChoiceItemsForSelectedGroup.map((item) => (
                <option key={item.correlationId} value={item.correlationId}>
                  {item.englishName} ({cents(item.priceCents)})
                </option>
              ))}
            </select>
            <button
              className="h-10 rounded-lg border border-[#FD7657] px-4 text-sm font-medium text-[#FD7657] disabled:cursor-not-allowed disabled:border-[#E5E7EB] disabled:text-[#9CA3AF]"
              onClick={addChoiceItem}
              disabled={!selectedChoiceGroupId || !selectedChoiceItemId}
            >
              Add Item To Group
            </button>
          </div>
        </section>

        <section className="rounded-2xl border border-[#E5E7EB] bg-white p-4 shadow-sm">
          <h2 className="text-base font-semibold text-[#111827]">Quick Service Plates</h2>
          <p className="mt-1 text-xs text-[#6b7280]">
            Plates are the POS buttons, for example 1 Entree, 2 Entrees, or 3 Entrees. They are not regular fixed combos.
          </p>
          <div className="mt-4 grid gap-2">
            <div className="grid grid-cols-[1fr_140px_140px_auto] gap-2">
              <input
                className="h-10 rounded-lg border border-[#D1D5DB] px-3 text-sm"
                value={comboName}
                onChange={(event) => setComboName(event.target.value)}
                placeholder="e.g. 1 Entree, 2 Entrees"
              />
              <div className="flex h-10 items-center rounded-lg border border-[#D1D5DB] bg-white px-3 text-sm">
                <span className="mr-1 text-[#6B7280]">$</span>
                <input
                  className="h-full min-w-0 flex-1 border-0 p-0 text-sm outline-none"
                  value={comboPrice}
                  onChange={(event) => setComboPrice(event.target.value)}
                  placeholder="Price"
                  type="number"
                  min={0}
                  step="0.01"
                />
              </div>
              <input
                className="h-10 rounded-lg border border-[#D1D5DB] px-3 text-sm"
                value={comboTaxRatePercent}
                onChange={(event) => setComboTaxRatePercent(event.target.value)}
                placeholder="Tax %"
                type="number"
                min={0}
                step="0.01"
              />
              <button className="h-10 rounded-lg bg-[#FD7657] px-4 text-sm font-medium text-white" onClick={createCombo}>
                Add Plate
              </button>
            </div>
          </div>

          <div className="mt-5 space-y-3">
            {combos.map((combo) => {
              const selected = combo.correlationId === selectedComboItemId;
              const editing = combo.correlationId === editingComboId;
              return (
                <div
                  key={combo.correlationId}
                  className={`w-full rounded-xl border p-3 text-left transition ${
                    selected ? "border-[#FD7657] bg-[#FEF5F4]" : "border-[#E5E7EB] bg-white"
                  }`}
                  onClick={() => setSelectedComboItemId(combo.correlationId)}
                >
                  {editing ? (
                    <div className="grid gap-2" onClick={(event) => event.stopPropagation()}>
                      <div className="grid grid-cols-[1fr_120px_120px] gap-2">
                        <input
                          className="h-9 rounded-lg border border-[#D1D5DB] px-3 text-sm"
                          value={editingComboName}
                          onChange={(event) => setEditingComboName(event.target.value)}
                          placeholder="Plate name"
                        />
                        <div className="flex h-9 items-center rounded-lg border border-[#D1D5DB] bg-white px-3 text-sm">
                          <span className="mr-1 text-[#6B7280]">$</span>
                          <input
                            className="h-full min-w-0 flex-1 border-0 p-0 text-sm outline-none"
                            value={editingComboPrice}
                            onChange={(event) => setEditingComboPrice(event.target.value)}
                            placeholder="Price"
                            type="number"
                            min={0}
                            step="0.01"
                          />
                        </div>
                        <input
                          className="h-9 rounded-lg border border-[#D1D5DB] px-3 text-sm"
                          value={editingComboTaxRatePercent}
                          onChange={(event) => setEditingComboTaxRatePercent(event.target.value)}
                          placeholder="Tax %"
                          type="number"
                          min={0}
                          step="0.01"
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          className="rounded-lg border border-[#D1D5DB] px-3 py-1.5 text-xs font-medium text-[#6B7280]"
                          onClick={cancelEditingCombo}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="rounded-lg bg-[#FD7657] px-3 py-1.5 text-xs font-medium text-white"
                          onClick={updateCombo}
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-3">
                      <div className="font-medium text-[#111827]">
                        {combo.name} <span className="text-sm font-normal text-[#6b7280]">{cents(combo.priceCents)} · Tax {percent(combo.taxRate)}</span>
                      </div>
                      <div className="flex shrink-0 gap-2" onClick={(event) => event.stopPropagation()}>
                        <button
                          type="button"
                          className="text-xs font-medium text-[#FD7657]"
                          onClick={() => startEditingCombo(combo)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="text-xs font-medium text-[#DC2626]"
                          onClick={() => deleteCombo(combo.correlationId)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="mt-2 space-y-1 text-sm text-[#6b7280]">
                    {(combo.comboGroups ?? []).length === 0 ? (
                      <div>No rules attached yet.</div>
                    ) : (
                      (combo.comboGroups ?? []).map((group) => (
                        <div key={group.correlationId}>
                          {group.displayName || group.choiceGroup?.name}: choose {group.minSelections === group.maxSelections ? group.minSelections : `${group.minSelections}-${group.maxSelections}`}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <h2 className="mt-6 text-base font-semibold text-[#111827]">Plate Rules</h2>
          <p className="mt-1 text-xs text-[#6b7280]">
            Pick one plate, pick one reusable choice group, then set how many choices are required for that plate.
          </p>
          <div className="mt-4 grid gap-2">
            <select className="h-10 rounded-lg border border-[#D1D5DB] px-3 text-sm" value={selectedComboItemId} onChange={(event) => setSelectedComboItemId(event.target.value)}>
              <option value="">Select quick-service plate</option>
              {combos.map((combo) => <option key={combo.correlationId} value={combo.correlationId}>{combo.name} ({cents(combo.priceCents)})</option>)}
            </select>
            <select className="h-10 rounded-lg border border-[#D1D5DB] px-3 text-sm" value={selectedComboChoiceGroupId} onChange={(event) => setSelectedComboChoiceGroupId(event.target.value)}>
              <option value="">Select reusable choice group</option>
              {choiceGroups.map((group) => <option key={group.correlationId} value={group.correlationId}>{group.name}</option>)}
            </select>
            <input className="h-10 rounded-lg border border-[#D1D5DB] px-3 text-sm" value={comboGroupName} onChange={(event) => setComboGroupName(event.target.value)} placeholder="Display label, e.g. Choose entree" />
            <div className="grid grid-cols-2 gap-2">
              <label className="grid gap-1 text-xs font-medium text-[#6b7280]">
                Minimum choices
                <input className="h-10 rounded-lg border border-[#D1D5DB] px-3 text-sm font-normal text-[#111827]" type="number" min={0} value={minSelections} onChange={(event) => setMinSelections(Number(event.target.value))} />
              </label>
              <label className="grid gap-1 text-xs font-medium text-[#6b7280]">
                Maximum choices
                <input className="h-10 rounded-lg border border-[#D1D5DB] px-3 text-sm font-normal text-[#111827]" type="number" min={1} value={maxSelections} onChange={(event) => setMaxSelections(Number(event.target.value))} />
              </label>
            </div>
            <button className="h-10 rounded-lg bg-[#FD7657] px-4 text-sm font-medium text-white" onClick={attachChoiceGroupToCombo}>
              Attach Group To Plate
            </button>
          </div>
        </section>
      </div>

      {loading && <div className="text-sm text-[#6b7280]">Loading quick service setup...</div>}
    </div>
  );
}
